import { useQuery, QueryClient } from 'react-query';

import type { QueryFunction } from '@frontend/api';
import { useRequest } from '@frontend/api';

export type Subquote = {
  id: number;
  site: {
    id: number;
    name: string;
    businessName: string;
  };
  quoteNumber: number;
  requestedOn: string;
  finalizedOn: string | null;
  note: string | null;
  sender: {
    id: number;
    firstName: string;
    lastName: string;
  } | null;
  lineItems: number[];
  userCanAccess: boolean;
};

export type SubquotesList = Subquote[];

export type UseGetSubquotesListParams = {
  quoteId: number;
};

export type GetSubquotesListQueryKeyType = readonly ['subquotesList', number];

export const getSubquotesListQueryKey = (
  quoteId: number,
): GetSubquotesListQueryKeyType => {
  return ['subquotesList', quoteId];
};

export const invalidateSubquotesListQuery = (
  queryClient: QueryClient,
  quoteId?: number | null,
) => {
  if (quoteId != null) {
    queryClient.invalidateQueries(getSubquotesListQueryKey(quoteId));
  }
};

export const useGetSubquotesList: QueryFunction<
  SubquotesList,
  [UseGetSubquotesListParams],
  GetSubquotesListQueryKeyType
> = (params, { requestOptions, queryOptions } = {}) => {
  const { quoteId } = params;
  const request = useRequest(requestOptions);
  const queryKey = getSubquotesListQueryKey(quoteId);

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) => {
      return request<SubquotesList>(`/quotes/${quoteId}/subquotes`, {
        init: {
          method: 'GET',
          signal,
        },
      });
    },
  });
};
